import { createContext, useContext, type ReactNode } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useLocation } from 'react-router';
import { z } from 'zod';

import { useAuth } from '@/hooks/auth/use-auth';
import { QUERY_KEYS } from '@/config/queries';
import { supabase } from '@/integrations/supabase/client';
import { loadWebMfaGateState, type WebMfaGateState } from '@/lib/auth-mfa-gate';
import { requiresCompanyMembership } from '@/lib/auth-redirect';
import { loadHealthConsentPreferences, type ConsentPreferences } from '@/lib/health-consent-preferences';

type WebAccess = {
  mfaQuery: UseQueryResult<WebMfaGateState, Error>;
  memberQuery: UseQueryResult<boolean, Error>;
  consentQuery: UseQueryResult<ConsentPreferences, Error>;
  healthReady: boolean;
  readinessGeneration: number;
};

const WebAccessContext = createContext<WebAccess | null>(null);
const admissionRows = z.array(z.object({ company_id: z.string().uuid() })).max(1);

// eslint-disable-next-line react-refresh/only-export-components
export function useWebAccess(): WebAccess {
  const access = useContext(WebAccessContext);
  if (!access) throw new Error('WebAccessProvider required');
  return access;
}

export function WebAccessProvider({ children }: { children: ReactNode }) {
  const { user, session, loading, accessVersion } = useAuth();
  const location = useLocation();
  const userId = user?.id ?? session?.user.id ?? '';
  const mfaQuery = useQuery({
    queryKey: QUERY_KEYS.MFA_GATE(userId, accessVersion),
    queryFn: loadWebMfaGateState,
    enabled: Boolean(userId && !loading),
    retry: false,
    // Consumers can initially suspend. Subscribe here to error/loading changes
    // rather than relying on which result properties a child has read.
    notifyOnChangeProps: ['data', 'error', 'status', 'fetchStatus'],
    staleTime: Number.POSITIVE_INFINITY,
  });
  const mfaReady = Boolean(userId && !loading && mfaQuery.isSuccess && mfaQuery.fetchStatus === 'idle' && !mfaQuery.data.required);
  const personalOverview = location.pathname === '/profile' &&
    !['privacy', 'vault'].includes(new URLSearchParams(location.search).get('section') ?? 'overview');
  const needsHealthAccess = requiresCompanyMembership(location.pathname, location.search) || personalOverview;
  const memberQuery = useQuery({
    queryKey: QUERY_KEYS.MEMBER_ADMISSION(userId, accessVersion),
    queryFn: async ({ signal }) => {
      const { data, error } = await supabase.from('company_employees').select('company_id')
        .eq('user_id', userId).eq('is_active', true).limit(1).abortSignal(signal);
      if (error) throw error;
      // Employee eligibility requires at least one active membership, as on native.
      return admissionRows.parse(data).length > 0;
    },
    enabled: mfaReady && needsHealthAccess,
    retry: false,
    notifyOnChangeProps: ['data', 'error', 'status', 'fetchStatus'],
    staleTime: Number.POSITIVE_INFINITY,
  });
  const memberReady = mfaReady && needsHealthAccess && memberQuery.isSuccess && memberQuery.fetchStatus === 'idle' && memberQuery.data === true;
  const consentQuery = useQuery({
    queryKey: QUERY_KEYS.HEALTH_CONSENT(userId, accessVersion),
    queryFn: ({ signal }) => loadHealthConsentPreferences(userId, signal),
    enabled: memberReady,
    retry: false,
    notifyOnChangeProps: ['data', 'error', 'status', 'fetchStatus'],
    staleTime: Number.POSITIVE_INFINITY,
  });
  const healthReady = memberReady && consentQuery.isSuccess && consentQuery.fetchStatus === 'idle' && consentQuery.data.consent_data_processing;

  return <WebAccessContext.Provider value={{ mfaQuery, memberQuery, consentQuery, healthReady, readinessGeneration: accessVersion }}>
    {children}
  </WebAccessContext.Provider>;
}
